import InfoIcon from "@mui/icons-material/Info";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import { Grid, IconButton } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import UniversalToolBarWithDialogs from "../../components/bar/UniversalToolBarWithDialogs";
import { ClientPassportButton } from "../../components/buttons/ClientPassportButton";
import FilterTable from "../../components/table/FilterTable";
import DefaultPageWrapper from "../../components/wrapper/DefaultPageWrapper";
import LoaderWrapper from "../../components/wrapper/LoaderWrapper";
import { DATE_RANGE_FIELD_TYPE, TEXT_FIELD_TYPE } from "../../helpers/constants";
import { useDialogBetter } from "../../hooks/useDialogBetter";
import usePaginationWithSearchParams from "../../hooks/usePaginationWithSearchParams";
import { useGetPersonsRepository } from "../../services/personRepositoryService";
import TableService from "../../services/tableService";
import { ContactsDialog } from "./ContactsDialog";
import { EditConnectedPersonDialog } from "./EditConnectedPersonDialog";

export const PERSONS_REPOSITORY_TABLE_NAME = "customers_table";

export function PersonRepositoryPage(props) {
    const { pageName } = props;
    // const dialogs = useDialogs

    const { t } = useTranslation();
    // check permission if is required see useCheckPermission hook
    // const [hasEditPermission] = useCheckPermission(EDIT_PERMISSION)
    const [hasEditPermission] = [true];

    const [dataLocal, setDataLocal] = useState([]);
    const [recordsCount, setRecordsCount] = useState(0);
    const [selectedRowId, setSelectedRowId] = useState();
    const [hiddenColumnsForTables, setHiddenColumnsForTables] = useState([]);
    const [columnsOrdersForTables, setColumnsOrdersForTables] = useState([]);
    const [checkedAll, setCheckedAll] = useState(false);

    const { openDialog: openCustomerPassport, closeDialog: closeCustomerPassport } =
        useDialogBetter();
    const {
        openDialog: openEditConnectedPersonDialog,
        closeDialog: closeEditConnectedPersonDialog,
    } = useDialogBetter();
    // contacts Diloag
    const { openDialog: openContactsDialog, closeDialog: closeContactsDialog } = useDialogBetter();

    const {
        resetPageNumber,
        searchParams,
        page: dataPage,
        pageSize: dataPageSize,
        handleChangePageWithSearchParams: handleChangeDataPage,
        handleChangePageSizeWithSearchParams: handleChangeDataPageSize,
    } = usePaginationWithSearchParams();

    const isFooTableLoading = false; // Set loading when data are preparing

    const PERSONS_REPOSITORY_TABLE_FIELDS_CONFIG = useMemo(() => {
        return [
            {
                name: "passportBtn",
                getValueCallback: (rowData) => {
                    if (rowData.customer_id) {
                        return (
                            <ClientPassportButton
                                clientId={rowData.customer_id}
                                clientType="customer"
                            />
                        );
                    }
                    return (
                        <IconButton
                            color="primary"
                            onClick={() => {
                                openEditConnectedPersonDialog({
                                    Component: EditConnectedPersonDialog,
                                    componentProps: {
                                        personId: rowData.id,
                                    },
                                    onCloseFn: () => setSelectedRowId(undefined),
                                });
                            }}
                        >
                            <InfoIcon />
                        </IconButton>
                    );
                },
                label: t(""),
                // @ts-ignore
                getFilterFieldConfigCallback: (filteringData) => undefined,
                sortable: false,
                excludedFromExport: true,
                allowChangeVisibility: false,
            },
            {
                name: "fname",
                getValueCallback: (rowData) => rowData.name,
                label: t("persons.table.firstName"),
                filterType: TEXT_FIELD_TYPE,
                getFilterFieldConfigCallback: (filteringData) => undefined,
                sortable: true,
                minWidth: 120,
                width: 120,
            },
            {
                name: "lname",
                getValueCallback: (rowData) => rowData.surname,
                label: t("persons.table.lastName"),
                filterType: TEXT_FIELD_TYPE,
                getFilterFieldConfigCallback: (filteringData) => undefined,
                sortable: true,
                minWidth: 120,
                width: 120,
            },
            {
                name: "email",
                getValueCallback: (rowData) => rowData.email.email_address,
                label: t("persons.table.email"),
                filterType: TEXT_FIELD_TYPE,
                getFilterFieldConfigCallback: (filteringData) => undefined,
                sortable: false,
            },
            {
                name: "phone",
                getValueCallback: (rowData) => rowData.phone.phone_number,
                label: t("persons.table.phoneNumber"),
                filterType: TEXT_FIELD_TYPE,
                getFilterFieldConfigCallback: (filteringData) => undefined,
                sortable: false,
            },
            {
                name: "birthdate",
                getValueCallback: (rowData) => {
                    if (!rowData.birth_date) {
                        return "";
                    }
                    // return DateService.convertDatetimeFromBackendToFormatYYYYMMDD(rowData.birth_date)
                    return new String(rowData.birth_date); // TODO
                },
                label: t("persons.table.birthDate"),
                filterType: DATE_RANGE_FIELD_TYPE,
                getFilterFieldConfigCallback: (filteringData) => ({
                    style: { minWidth: "100px" },
                }),
                sortable: false,
            },
            {
                name: "contact",
                getValueCallback: (rowData) => (
                    <IconButton
                        color="primary"
                        onClick={() => {
                            openContactsDialog({
                                Component: ContactsDialog,
                                componentProps: {
                                    personId: rowData.id,
                                },
                            });
                        }}
                    >
                        <LocalPhoneIcon />
                    </IconButton>
                ),
                label: t("persons.table.contact"),
                // @ts-ignore
                getFilterFieldConfigCallback: (filteringData) => undefined,
                sortable: false,
                excludedFromExport: true,
                allowChangeVisibility: false,
                // minWidth: 120,
                // width: 120,
            },
        ];
    }, [openEditConnectedPersonDialog, openContactsDialog]);

    const customerDataExportHeaders = useMemo(() => {
        return TableService.getTableExportHeaders(
            PERSONS_REPOSITORY_TABLE_FIELDS_CONFIG, // @ts-ignore
            hiddenColumnsForTables?.[PERSONS_REPOSITORY_TABLE_NAME], // @ts-ignore
            columnsOrdersForTables?.[PERSONS_REPOSITORY_TABLE_NAME],
        );
    }, [
        // @ts-ignore
        hiddenColumnsForTables?.[PERSONS_REPOSITORY_TABLE_NAME],
        // @ts-ignore
        columnsOrdersForTables?.[PERSONS_REPOSITORY_TABLE_NAME],
        PERSONS_REPOSITORY_TABLE_FIELDS_CONFIG,
    ]);

    const { data: personsRows, refetch, isLoading } = useGetPersonsRepository(searchParams);

    useEffect(() => {
        if (personsRows?.results) {
            setDataLocal(personsRows.results);
            setRecordsCount(personsRows.count);
        }
    }, [personsRows, dataPage, dataPageSize]);

    // @ts-ignore
    const onClickButton = useCallback((e, id) => {
        e.stopPropagation();
        // This function can be used for row buttons if needed
    }, []);

    const data = useMemo(
        () =>
            TableService.getPreparedDataForCollapsibleTable(
                PERSONS_REPOSITORY_TABLE_FIELDS_CONFIG,
                dataLocal, // @ts-ignore
                hiddenColumnsForTables?.[PERSONS_REPOSITORY_TABLE_NAME]
                    ? // @ts-ignore
                      hiddenColumnsForTables?.[PERSONS_REPOSITORY_TABLE_NAME]
                    : [], // @ts-ignore
                columnsOrdersForTables?.[PERSONS_REPOSITORY_TABLE_NAME]
                    ? // @ts-ignore
                      columnsOrdersForTables?.[PERSONS_REPOSITORY_TABLE_NAME]
                    : [],
                onClickButton,
            ),
        [
            PERSONS_REPOSITORY_TABLE_FIELDS_CONFIG,
            dataLocal,
            hiddenColumnsForTables,
            columnsOrdersForTables,
            onClickButton,
        ],
    );

    useEffect(() => {
        refetch();
    }, [searchParams, refetch]);

    return (
        // @ts-ignore
        <DefaultPageWrapper tagTitle={t("headers.persons")}>
            <Grid
                item
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                rowSpacing={1}
            >
                <Grid item xs={12} marginY={1}>
                    <UniversalToolBarWithDialogs
                        pageName={pageName}
                        tableConfig={{
                            name: PERSONS_REPOSITORY_TABLE_NAME,
                            config: PERSONS_REPOSITORY_TABLE_FIELDS_CONFIG,
                        }}
                        filteringData={() => {}}
                        refreshTable={refetch}
                        // @ts-ignore
                        resetPageNumber={resetPageNumber}
                        hiddenColumnsForTables={hiddenColumnsForTables}
                        setHiddenColumnsForTables={setHiddenColumnsForTables}
                        columnsOrdersForTables={columnsOrdersForTables}
                        setColumnsOrdersForTables={setColumnsOrdersForTables}
                        showMassActionButton={false}
                        showOpenCloseFilterButton={false}
                        showCleanfiltersButton={true}
                        showMyFiltersButton={true}
                        showSearchButton={true}
                        itemType={"customerPerson"}
                        style={{ marginBlock: "5px" }}
                        // We have separate buttons for creating company and person
                        showCreateButton={false}
                        mdGridButton={hasEditPermission ? 1.25 : 2.75}
                        showImportList={false}
                        showRemoveLeadButton={false}
                    />
                </Grid>
                <Grid item xs={12}>
                    <LoaderWrapper showLoader={isFooTableLoading}>
                        <FilterTable
                            // @ts-ignore
                            data={data}
                            countRecords={recordsCount}
                            resetPageNumber={resetPageNumber}
                            selectedItemId={selectedRowId}
                            style={{ maxHeight: "60vh" }}
                            headersConfig={TableService.getHeadersConfigForCollapsibleTable(
                                PERSONS_REPOSITORY_TABLE_FIELDS_CONFIG,
                                [], // @ts-ignore
                                hiddenColumnsForTables?.[PERSONS_REPOSITORY_TABLE_NAME]
                                    ? // @ts-ignore
                                      hiddenColumnsForTables?.[PERSONS_REPOSITORY_TABLE_NAME]
                                    : [], // @ts-ignore
                                columnsOrdersForTables?.[PERSONS_REPOSITORY_TABLE_NAME]
                                    ? // @ts-ignore
                                      columnsOrdersForTables?.[PERSONS_REPOSITORY_TABLE_NAME]
                                    : [],
                            )}
                            showCleanFilterIcon={true}
                            showContextMenu={false}
                            showExportToFileButton={true}
                            exportToFileUrl={"customers/person-index/export/"}
                            exportToFileSearchParams={searchParams}
                            exportToFileHeaders={customerDataExportHeaders}
                            exportToFileFileName={`${t("persons.exportFileName")}.xlsx`}
                            showDetailsIcon={false}
                            showCheckbox={false}
                            checkedAll={checkedAll}
                            page={dataPage}
                            onPageChange={handleChangeDataPage}
                            rowsPerPage={dataPageSize}
                            onRowsPerPageChange={handleChangeDataPageSize}
                            setDataLocal={setDataLocal}
                            setCheckedAll={setCheckedAll}
                            setSelectedRowId={setSelectedRowId}
                            isLoading={isLoading}
                        />
                    </LoaderWrapper>
                </Grid>
            </Grid>
        </DefaultPageWrapper>
    );
}
